import { useAuftragRepository } from '@Hooks/useRepository';
import type { ApiStorageStockEntry } from '@Model';
import { StorageStockRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

const STORAGE_STOCK_QUERY = 'storage-stock';

const STOCK_UNREADABLE = 'Der Bestand konnte nicht geladen werden.';

export interface StorageStockState {
    entries: ApiStorageStockEntry[];
    isLoading: boolean;
    /** Why the stock could not be read, empty while it could. */
    failure: string;
    reload: () => void;
}

interface IRequest {
    storageLocationId: string | undefined;
    /** The instant the stock is read at; now when undefined. */
    validAt: string | undefined;
    /** A booking of that instant: bookings that sort after it are left out of the stock. */
    bookingId?: string;
    enabled?: boolean;
}

/** The stock of a Lagerort per Material and AVV at an instant. */
// @ai-generated 2026-10-23T14:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useStorageStock({
    storageLocationId,
    validAt,
    bookingId,
    enabled = true,
}: IRequest): StorageStockState {
    const repository = useAuftragRepository(StorageStockRepository);

    const { data, isPending, refetch } = useQuery({
        queryKey: [STORAGE_STOCK_QUERY, storageLocationId, validAt ?? 'now', bookingId],
        queryFn: () => repository.getStock(storageLocationId ?? '', validAt ?? new Date().toISOString(), bookingId),
        enabled: enabled && !!storageLocationId,
    });

    const reload = useCallback(() => void refetch(), [refetch]);

    if (data === undefined) {
        return { entries: [], isLoading: enabled && isPending, failure: '', reload };
    }
    if (!isOk(data)) {
        return { entries: [], isLoading: false, failure: data.messages?.[0]?.message ?? STOCK_UNREADABLE, reload };
    }
    return { entries: data.data, isLoading: false, failure: '', reload };
}
